
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { EventBus } from '@/main';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import Papa from 'papaparse';

@Component
export default class ImportItemsDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly dialog!: boolean;
  @Prop({ type: [String, Number], required: true }) readonly purchaseOrderId!: string | number;
  @Prop({ type: String, default: null }) readonly poCurrencyCode!: string | null;

  /**
   * US-CURR-013: a CSV cost is imported as-is into a PO denominated in the SUPPLIER's
   * currency. This label used to say "in the store's currency", which actively told
   * buyers to enter the wrong number — the import never converted it.
   */
  private get costColumnLabel(): string {
    const currency = this.poCurrencyCode || CurrencyHelper.getStoreCurrency();
    return `Which column is the cost (base) price, in the purchase order's currency (${currency})?`;
  }

  private importFile: File | null = null;
  private importing = false;
  private downloadingTemplate = false;
  private fileError = '';
  private importSummaryData: any = {};
  private showImportSummaryDialog = false;

  // CSV preview & column mapping
  private csvHeaders: string[] = [];
  private csvPreviewRows: any[] = [];
  private csvParsed = false;
  private mappingIdentifierColumn: string | null = null;
  private mappingIdentifierType: string | null = null;
  private mappingQuantityColumn: string | null = null;
  private mappingCostColumn: string | null = null;
  private mappingSupplierSkuColumn: string | null = null;
  private mappingNoteColumn: string | null = null;

  private identifierTypeOptions = [
    { text: 'SKU', value: 'sku' },
    { text: 'Barcode', value: 'barcode' },
    { text: 'Shopify Variant ID', value: 'variant_id' },
  ];

  private get csvHeaderOptionsWithNone(): Array<{ text: string; value: string | null }> {
    return [
      { text: '— None —', value: null },
      ...this.csvHeaders.map(h => ({ text: h, value: h })),
    ];
  }

  private get isColumnMappingValid(): boolean {
    return !!(this.mappingIdentifierColumn && this.mappingIdentifierType && this.mappingQuantityColumn);
  }

  private get isImportReady(): boolean {
    return !!(this.importFile && this.csvParsed && this.isColumnMappingValid);
  }

  @Watch('dialog')
  onDialogChange(newVal: boolean) {
    if (newVal) {
      this.resetState();
    }
  }

  resetState() {
    this.importFile = null;
    this.fileError = '';
    this.importing = false;
    this.downloadingTemplate = false;
    this.csvHeaders = [];
    this.csvPreviewRows = [];
    this.csvParsed = false;
    this.mappingIdentifierColumn = null;
    this.mappingIdentifierType = null;
    this.mappingQuantityColumn = null;
    this.mappingCostColumn = null;
    this.mappingSupplierSkuColumn = null;
    this.mappingNoteColumn = null;
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  closeDialog() {
    this.resetState();
    this.importSummaryData = {};
    this.showImportSummaryDialog = false;
    this.$emit('update:dialog', false);
  }

  closeSummaryDialog() {
    this.showImportSummaryDialog = false;
    // Always close the main dialog after reviewing the summary
    this.closeDialog();
  }

  async downloadTemplate() {
    this.downloadingTemplate = true;
    try {
      const blob = await PurchaseOrderModel.downloadItemsTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'purchase_order_items_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Download failed: ${error.message}`, 'error');
    } finally {
      this.downloadingTemplate = false;
    }
  }

  openFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement).click();
  }

  handleFileSelection(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      this.importFile = null;
      this.fileError = '';
      return;
    }

    const file = files[0];

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.fileError = 'Please select a valid CSV file.';
      this.importFile = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.fileError = 'File size must be less than 5MB.';
      this.importFile = null;
      return;
    }

    this.importFile = file;
    this.fileError = '';
    this.parseCSVPreview(file);
  }

  private parseCSVPreview(file: File) {
    Papa.parse<any>(file, {
      header: true,
      preview: 5,
      skipEmptyLines: true,
      complete: (results) => {
        this.csvHeaders = results.meta.fields || [];
        this.csvPreviewRows = results.data;
        this.csvParsed = true;

        if (this.csvHeaders.length === 0) {
          this.fileError = 'CSV file has no column headers.';
          this.csvParsed = false;
        }
      },
      error: (error) => {
        this.fileError = `Failed to parse CSV: ${error.message}`;
        this.csvParsed = false;
      }
    });
  }

  clearFileSelection() {
    this.importFile = null;
    this.fileError = '';
    this.csvHeaders = [];
    this.csvPreviewRows = [];
    this.csvParsed = false;
    this.mappingIdentifierColumn = null;
    this.mappingIdentifierType = null;
    this.mappingQuantityColumn = null;
    this.mappingCostColumn = null;
    this.mappingSupplierSkuColumn = null;
    this.mappingNoteColumn = null;
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private getErrorIdentifier(error: any): string {
    if (!error.data) return '-';
    if (this.mappingIdentifierColumn && error.data[this.mappingIdentifierColumn]) {
      return error.data[this.mappingIdentifierColumn];
    }
    return error.data.sku || '-';
  }

  async importItems() {
    if (!this.importFile || !this.isColumnMappingValid) return;

    this.importing = true;
    try {
      const columnMapping = {
        identifier: this.mappingIdentifierColumn,
        identifierType: this.mappingIdentifierType,
        quantity: this.mappingQuantityColumn,
        unitPrice: this.mappingCostColumn || null,
        supplierSku: this.mappingSupplierSkuColumn || null,
        note: this.mappingNoteColumn || null,
      };

      const po = new PurchaseOrderModel({ id: Number(this.purchaseOrderId) });
      const response = await po.importItems(this.importFile, columnMapping);

      const data = response?.data || response || {};
      this.importSummaryData = data;

      // Always emit success so parent reloads the item list
      this.$emit('import-success');

      // Show snackbar and close dialog
      const successCount = data.successfulRows || 0;
      const failedCount = data.failedRows || 0;

      if (failedCount > 0) {
        // Show summary dialog for errors, then close main dialog when user clicks OK
        this.showImportSummaryDialog = true;
      } else {
        EventBus.$emit('show-snackbar', {
          message: `Import complete: ${successCount} items imported`,
          color: 'success',
        });
        this.closeDialog();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      this.fileError = `Import failed: ${message}`;
      if (error.response?.data?.errorDetails) {
        this.importSummaryData = error.response.data;
        this.showImportSummaryDialog = true;
      } else {
        EventBus.$emit('show-snackbar', this.fileError, 'error');
      }
    } finally {
      this.importing = false;
    }
  }
}
